/* eslint-disable no-use-before-define */
import React, { useEffect } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';
import PerfectScrollbar from 'react-perfect-scrollbar';
import PropTypes from 'prop-types';
import axios from 'axios';
import Package from '../../../../package.json'
import { useSnackbar } from 'notistack';
import {
  Avatar,
  Box,
  Button,
  Divider,
  Drawer,
  Hidden,
  Link,
  List,
  ListSubheader,
  makeStyles,
} from '@material-ui/core';
import ReceiptIcon from '@material-ui/icons/ReceiptOutlined';
import {
  Briefcase as BriefcaseIcon,
  AlertCircle as AlertCircleIcon,
  Edit as EditIcon,
  DollarSign as DollarSignIcon,
  PieChart as PieChartIcon,
  Gift as GiftIcon
} from 'react-feather';
import Logo from 'src/components/Logo';
import useAuth from 'src/hooks/useAuth';
import NavItem from './NavItem';

const sections = [
  {
    subheader: 'Reports',
    items: [
      {
        title: 'Dashboard',
        icon: PieChartIcon,
        href: '/dashboard',
      },
    ],
  },
  {
    subheader: 'Management',
    items: [
      {
        title: 'Ads',
        icon: DollarSignIcon,
        href: '/ads',
        items: [
          {
            title: 'Ads List',
            href: '/ads',
          },
          {
            title: 'Create Ad',
            href: '/ads/new',
          },
        ],
      },
      {
        title: 'Articles',
        icon: EditIcon,
        href: '/articles',
        items: [
          {
            title: 'Articles List',
            href: '/articles',
          },
          {
            title: 'Create Article',
            href: '/articles/new',
          },
        ],
      },
      {
        title: 'Business Listings',
        icon: BriefcaseIcon,
        href: '/businesslistings',
        items: [
          {
            title: 'Business Listings List',
            href: '/businesslistings',
          },
          {
            title: 'Create Business Listing',
            href: '/businesslistings/new',
          },
          {
            title: 'Create Category',
            href: '/businesslistings/newCat',
          },
        ],
      },
      {
        title: 'Specials',
        icon: GiftIcon,
        href: '/specials',
        items: [
          {
            title: 'Specials List',
            href: '/specials',
          },
          {
            title: 'Create Special',
            href: '/specials/new',
          },
        ],
      },
      {
        title: 'Classified Ads',
        icon: ReceiptIcon,
        href: '/classifieds',
        items: [
          {
            title: 'Classified Ads List',
            href: '/classifieds',
          },
          {
            title: 'Create New Classified Ads',
            href: '/classifieds/new',
          },
        ],
      },
      {
        title: 'Notices',
        icon: AlertCircleIcon,
        href: '/notices',
        items: [
          {
            title: 'Notices List',
            href: '/notices',
          },
          {
            title: 'Create Notice',
            href: '/notices/new',
          },
        ],
      },
    ],
  },
];

function renderNavItems({ items, pathname, depth = 0 }) {
  return (
    <List disablePadding>
      {items.reduce(
        (acc, item) => reduceChildRoutes({ acc, item, pathname, depth }),
        []
      )}
    </List>
  );
}

function reduceChildRoutes({ acc, pathname, item, depth }) {
  const key = item.title + depth;

  if (item.items) {
    const open = matchPath(pathname, {
      path: item.href,
      exact: false,
    });

    acc.push(
      <NavItem
        depth={depth}
        icon={item.icon}
        info={item.info}
        key={key}
        open={Boolean(open)}
        title={item.title}
      >
        {renderNavItems({
          depth: depth + 1,
          pathname,
          items: item.items,
        })}
      </NavItem>
    );
  } else {
    acc.push(
      <NavItem
        depth={depth}
        href={item.href}
        icon={item.icon}
        info={item.info}
        key={key}
        title={item.title}
      />
    );
  }

  return acc;
}

const useStyles = makeStyles(() => ({
  mobileDrawer: {
    width: 256,
  },
  desktopDrawer: {
    width: 256,
    top: 64,
    height: 'calc(100% - 64px)',
  },
  avatar: {
    cursor: 'pointer',
    width: 64,
    height: 64,
  },
}));

const NavBar = ({ onMobileClose, openMobile }) => {
  const classes = useStyles();
  const location = useLocation();
  const { user } = useAuth();

  const { enqueueSnackbar } = useSnackbar();

  function publish() {
    axios.post('https://api.netlify.com/build_hooks/5f69ff77eedd6a3a3bdc1eff', {})
      .then(() => enqueueSnackbar('Website is being published and should be live in a few minutes', {
        variant: 'success',
      }))
      .catch(err => enqueueSnackbar('Failed to publish website', {
        variant: 'error',
      }));
  }

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const content = (
    <Box height="100%" display="flex" flexDirection="column">
      <PerfectScrollbar options={{ suppressScrollX: true }}>
        <Hidden lgUp>
          <Box p={2} display="flex" justifyContent="center">
            <RouterLink to="/">
              <Logo height="25px" name="test" />
            </RouterLink>
          </Box>
        </Hidden>
        <Box p={2}>
          <Box display="flex" justifyContent="center">
            <RouterLink to="/app/account">
              <Avatar alt="User" className={classes.avatar} src={user.avatar} />
            </RouterLink>
          </Box>
          <Box mt={2} textAlign="center">
            <Link
              component={RouterLink}
              to="/app/account"
              variant="h5"
              color="textPrimary"
              underline="none"
            >
              {user.name}
            </Link>
          </Box>
        </Box>
        <Divider />
        <Box p={2}>
          {sections.map((section) => (
            <List
              key={section.subheader}
              subheader={
                <ListSubheader disableGutters disableSticky>
                  {section.subheader}
                </ListSubheader>
              }
            >
              {renderNavItems({
                items: section.items,
                pathname: location.pathname,
              })}
            </List>
          ))}
        </Box>
        <Divider />
        <Box p={2}>
          <Button variant="contained" color="primary" disableElevation fullWidth onClick={publish}>
            Publish
          </Button>
        </Box>
        <Box p={2}>
          {Package.version}
        </Box>
      </PerfectScrollbar>
    </Box>
  );

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          classes={{ paper: classes.mobileDrawer }}
          onClose={onMobileClose}
          open={openMobile}
          variant="temporary"
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <Drawer
          anchor="left"
          classes={{ paper: classes.desktopDrawer }}
          open
          variant="persistent"
        >
          {content}
        </Drawer>
      </Hidden>
    </>
  );
};

NavBar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
};

export default NavBar;
